import { useI18n } from '@muse/i18n'

import { getCollectionAudiosUrl } from '@core/api'
import { List, ListItem, Pagination } from '@ds/components'
import { AudioCardContainer } from '@features/audio-card'
import { buildListPlaylist } from '@features/audio-player'
import { type TOrderableList } from '@features/collection'

import { useCollectionTracklist } from '../hooks/useCollectionTracklist'
import { useCollectionScreenCollection } from '../store/useCollectionScreenCollection'
import { NoAudios } from '../components/collection-audios/NoAudios'

export const CollectionAudiosContainer = () => {
    const { t } = useI18n()
    const { id, authorUsername, slug } = useCollectionScreenCollection()
    const { items, pagination } = useCollectionTracklist()

    const playlist = buildListPlaylist({
        id,
        collectionId: id,
        items,
        pagination,
        url: getCollectionAudiosUrl(authorUsername, slug),
    })
    const orderableList: TOrderableList | undefined =
        pagination.total > 1
            ? { collectionId: id, authorUsername, slug, total: pagination.total }
            : undefined

    // Page 1 stays on the clean URL; the router refetches the loader (and remounts) on navigation.
    const getPageHref = (page: number) =>
        `/${authorUsername}/collections/${slug}${page > 1 ? `?page=${page}` : ''}`

    if (items.length === 0) {
        return (
            <NoAudios
                title={t('i18n.collectionEmptyTitle')}
                description={t('i18n.collectionEmptyDescription')}
            />
        )
    }

    return (
        <>
            <List>
                {items.map(audio => (
                    <ListItem key={audio.id}>
                        <AudioCardContainer
                            audio={audio}
                            placement="collection_audio_card"
                            playlist={playlist}
                            orderableList={orderableList}
                        />
                    </ListItem>
                ))}
            </List>
            {pagination.pages > 1 ? (
                <Pagination
                    currentPage={pagination.page}
                    lastPage={pagination.pages}
                    getPageHref={getPageHref}
                />
            ) : null}
        </>
    )
}
